<template>
  <v-container id="sub-orders-table" fluid>
    <v-card outlined :disabled="loadingData" :class="selected.length ? 'mb-12' : ''">
      <v-data-table
        :headers="computedHeaders"
        :items="computedSubOrders"
        :options.sync="options"
        :server-items-length="totalOrders"
        :footer-props="footerProps"
        :loading="loadingData"
        :loading-text="$t('order.ordersLoading')"
        :no-data-text="$t('order.noOrders')"
        mobile-breakpoint="650"
        item-key="_id"
        dense
        :show-select="status.length === 1 && status.includes('waiting') && pickPackShipActivated"
        v-model="selected"
        :singleSelect="false"
        :header-props="headerProps"
        :hide-default-footer="hideDefaultFooter"
      >
        <template v-slot:item.action="{ item, index }">
          <div class="d-flex align-center">
            <v-btn
              class="pr-1"
              color="primary"
              text
              @click="executeAction(item.order_id)"
              :disabled="!item.isSelectable"
            >
              <v-icon v-if="$vuetify.breakpoint.width >= 650 && $vuetify.breakpoint.width <= 768">mdi-magnify</v-icon>
              <span v-else>{{ item.action }}</span>
            </v-btn>

            <v-tooltip v-if="item.pick_pack_ship" v-model="showSubOrderInPPSTooltipFull[index]" top color="red">
              <template v-slot:activator="{ attrs }">
                <v-btn small dense icon v-bind="attrs" @click="togglePickPackShipTooltipFull(index)" class="ml-0 pl-0">
                  <v-icon color="red">mdi-information-outline</v-icon>
                </v-btn>
              </template>
              {{ getPickPackShipMessage(item.pick_pack_ship.picker, item.pick_pack_ship.reference) }}
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.date="{ item }">
          <span>{{ new Date(item.date) | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </template>

        <template v-slot:item.remainingTime="{ item }">
          <span v-if="item.remainingTime || item.remainingTime === 0">
            <span v-if="item.remainingTime <= 0" class="red--text">{{ $t('subOrder.timeout') }}</span>
            <countdown v-else :time="item.remainingTime" :transform="transform" :key="item.order_id">
              <template slot-scope="props">
                <span
                  v-if="props.days == 0 && props.hours == 0"
                  :class="props.minutes < 15 ? 'red--text' : 'orange--text'"
                >
                  {{ props.minutes }} min {{ props.seconds }}s
                </span>
                <span v-else>
                  <span v-if="props.days > 0">{{ props.days }} j</span>
                  {{ props.hours }} h {{ props.minutes }}
                </span>
              </template>
            </countdown>
          </span>
          <span v-else class="mdi mdi-infinity"></span>
        </template>

        <template v-slot:item.waitingAllocationTime="{ item }">
          <span v-if="item.waitingAllocationTime">
            <countdown :time="item.waitingAllocationTime" :transform="transform" :key="item.order_id">
              <template slot-scope="props">
                <span v-if="props.days >= 1" :class="props.days >= 2 ? 'red--text' : 'orange--text'">
                  {{ props.days }} {{ $t('subOrder.dayShortCut') }} {{ props.hours }} {{ $t('subOrder.hourShortCut') }}
                  {{ props.minutes }} {{ $t('subOrder.minuteShortCut') }}
                </span>
                <span v-else>
                  {{ props.hours }} {{ $t('subOrder.hourShortCut') }} {{ props.minutes }}
                  {{ $t('subOrder.minuteShortCut') }}
                </span>
              </template>
            </countdown>
          </span>
          <span v-else class="mdi mdi-infinity"></span>
        </template>

        <template v-slot:item.current_status="{ item }">
          <span>{{ getStatusTranslation(item.current_status) }}</span>
        </template>

        <template v-slot:item.type="{ item }">
          <span v-if="item.type">{{ getTypeTranslation(item.type) }}</span>
          <span v-else>X</span>
        </template>

        <template v-slot:item.order_id="{ item }">
          <p :style="orderIdColumn" class="mb-0">{{ item.order_id }}</p>
        </template>

        <template v-slot:item.stock="{ item }">
          <v-badge inline dot left :color="getStockColor(item.preparation_percent)">
            <span>{{ item.stock }}</span>
          </v-badge>
        </template>

        <template v-slot:top>
          <v-toolbar flat>
            <v-btn color="primary" outlined class="mb-2" @click="displayFilters = !displayFilters">
              <v-badge inline :value="nbActiveFilters" :content="nbActiveFilters">
                <span v-if="displayFilters">{{ $t('subOrder.hideFilters') }}</span>
                <span v-else>{{ $t('subOrder.displayFilters') }}</span>
              </v-badge>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              v-if="selected.length && $vuetify.breakpoint.width > 649"
              color="primary"
              outlined
              class="mb-2 mr-1"
              @click="$router.push({ name: 'BinSummary', params: { data: selected } })"
            >
              {{ $t('subOrder.bulkPreparation') }}
            </v-btn>
            <v-btn color="primary" outlined class="mb-2" @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>
      </v-data-table>
      <filter-dialog
        :key="formFiltersKey"
        v-model="displayFilters"
        :title="$t('order.filterOrders')"
        :fields="formFilter"
        @confirm="filter"
        :loadingData="loadingData"
      />
    </v-card>
    <v-bottom-navigation
      height="40"
      background-color="black"
      dark
      fixed
      v-if="selected.length && $vuetify.breakpoint.width <= 649"
    >
      <v-row
        class="justify-center align-center"
        @click="$router.push({ name: 'BinSummary', params: { data: selected } })"
        style="cursor: pointer"
      >
        <span>{{ $t('subOrder.bulkPreparation') }}</span>
      </v-row>
    </v-bottom-navigation>
  </v-container>
</template>

<script>
  import { mapState, mapMutations } from 'vuex'
  import { EventBus } from '../../event-bus'
  import notSetOptionMixin from '@/mixins/notSetOption'

  export default {
    name: 'SubOrdersTable',
    mixins: [notSetOptionMixin],
    data() {
      return {
        subOrders: [],
        totalOrders: 0,
        options: {
          itemsPerPage: 15,
          sortDesc: [], // fix the default value change in new vuetify version
        },
        loadingData: false,
        displayFilters: false,
        filters: [],
        formFiltersKey: Date.now(),
        componentPreviousPath: '',
        displayFieldSettings: null,
        hideDefaultFooter: false,
        selected: [],
        displayBinModal: false,
        pickPackShipActivated: false,
        showSubOrderInPPSTooltipFull: [],
      }
    },
    props: {
      status: {
        type: Array,
        default: () => [],
        required: true,
      },
      types: {
        type: Array,
        default: () => [],
        required: false,
      },
    },
    async mounted() {
      await this.getSettingsByCountry('FRA')
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
      $props: {
        // any change on props reload datas, use separate watcher for each prop did not work (double event)
        handler() {
          let translatedStatus = []
          for (var sts of this.status) {
            translatedStatus.push(this.statusTranslations.find((statusTranslation) => statusTranslation.value === sts))
          }
          this.formFilter.find((header) => header.value === 'current_status').arrayValues = translatedStatus

          let translatedTypes = []

          for (let type of this.types) {
            translatedTypes.push(this.typeTranslations.find((typeTranslation) => typeTranslation.value === type))
          }
          this.formFilter.find((header) => header.value === 'type').arrayValues = translatedTypes

          this.subOrders = []
          this.selected = []
          this.formFiltersKey = Date.now()
          this.filters = []

          if (this.$store.state.previousPath.split('/')[1] === 'sub-orders') {
            this.list()
          }
        },
        deep: true,
        immediate: true,
      },
    },
    activated() {
      let previousPath = this.$store.state.previousPath.split('/')[1]
      // we reset filters if the previous page wasn't the details of a sub-order OR if vue recycled it from a different instance
      if (
        previousPath !== 'sub-orders' ||
        (previousPath === 'sub-orders' && this.componentPreviousPath !== this.$route.path)
      ) {
        this.formFiltersKey = Date.now()
        this.filters = []
        this.options.page = 1
        this.options.itemsPerPage = 15
        this.options.sortBy = []
        this.options.sortDesc = []
      }
      this.subOrders = []
      this.list()
    },
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      ...mapState(['loading', 'currentStore', 'isCustomerService', 'roles']),
      computedSubOrders: function () {
        let computedSubOrders = this.subOrders
        computedSubOrders = computedSubOrders.map((subOrder) => this.compute(subOrder))
        return computedSubOrders
      },
      computedHeaders: function () {
        let computedHeaders = this.headers
        if (
          this.types.some((type) => ['click_and_collect', 'ship_from_store', 'click_and_reserve'].includes(type)) &&
          this.status.some((status) => ['waiting', 'confirmed'].includes(status))
        ) {
          if (this.$vuetify.breakpoint.width < 769) {
            computedHeaders = this.headersMobile
          } else {
            computedHeaders = this.headers
          }
        } else {
          if (this.$vuetify.breakpoint.width < 769 && this.$vuetify.breakpoint.width > 649) {
            computedHeaders = this.headersMobile.filter((header) => header.value !== 'remainingTime')
            computedHeaders = computedHeaders.filter((header) => header.value !== 'waitingAllocationTime')
          } else {
            computedHeaders = this.headers.filter((header) => header.value !== 'remainingTime')
          }
        }
        if (this.status.includes('waiting_allocation')) {
          computedHeaders = computedHeaders.filter((header) => header.value !== 'total_nb_product')
        } else {
          computedHeaders = computedHeaders.filter(
            (header) => header.value !== 'stock' && header.value !== 'waitingAllocationTime'
          )
        }
        return computedHeaders
      },
      nbActiveFilters: function () {
        return this.filters.filter((filter) => filter.active).length
      },
      statusTranslations() {
        return [
          { text: this.$t('order.waiting'), value: 'waiting' },
          { text: this.$t('order.waitingPayment'), value: 'waiting_payment' },
          { text: this.$t('order.confirmed'), value: 'confirmed' },
          { text: this.$t('order.partiallyConfirmed'), value: 'partially_confirmed' },
          { text: this.$t('order.toPickup'), value: 'to_pickup' },
          { text: this.$t('order.waitingCarrier'), value: 'waiting_carrier' },
          { text: this.$t('order.shippingInProgress'), value: 'shipping_in_progress' },
          { text: this.$t('order.partialShippingInProgress'), value: 'partial_shipping_in_progress' },
          { text: this.$t('order.delivered'), value: 'delivered' },
          { text: this.$t('order.deliveredPartially'), value: 'partially_delivered' },
          { text: this.$t('order.cancelled'), value: 'cancelled' },
          { text: this.$t('subOrder.waitingAllocation'), value: 'waiting_allocation' },
          { text: this.$t('order.blocked'), value: 'blocked' },
        ]
      },
      statusFilters() {
        const notFilterableStatus = ['waiting_allocation']
        return this.statusTranslations.filter((status) => !notFilterableStatus.includes(status.value))
      },
      typeTranslations() {
        return [
          { text: this.$t('subOrder.shipFromWarehouse'), value: 'ship_from_warehouse' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.inStorePickup'), value: 'in_store_pickup' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
          this.notSetOption,
        ]
      },
      formFilter() {
        let headers = [
          {
            text: this.$t('subOrder.numPreparation'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.type'),
            value: 'type',
            filterType: 'array',
            arrayValues: this.typeTranslations,
          },
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusFilters,
          },
          {
            text: this.$t('subOrder.origin'),
            value: 'origin',
            filterType: 'array',
            arrayValues: this.origin,
          },
          {
            text: this.$t('order.date'),
            value: 'date',
            filterType: 'date',
          },
          {
            text: this.$t('order.products'),
            value: 'total_nb_product',
            sortable: false,
          },
          {
            text: this.$t('subOrder.remainingTime'),
            value: 'remainingTime',
          },
        ]
        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push(
            {
              text: this.$t('order.client'),
              value: 'customer',
              filterType: 'text',
            },
            {
              text: this.$t('subOrder.lastNameShipping'),
              value: 'shipping_address.last_name',
              filterType: 'text',
            },
            {
              text: this.$t('subOrder.firstNameShipping'),
              value: 'shipping_address.first_name',
              filterType: 'text',
            }
          )
        }
        return headers
      },
      headers() {
        let headers = [
          {
            text: this.$t('subOrder.numPreparation'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.type'),
            value: 'type',
            filterType: 'array',
            arrayValues: this.typeTranslations,
          },
          {
            text: this.$t('subOrder.origin'),
            value: 'origin',
          },
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusTranslations,
          },
          {
            text: this.$t('order.date'),
            value: 'date',
            filterType: 'date',
          },
          {
            text: this.$t('order.products'),
            value: 'total_nb_product',
            sortable: false,
          },
        ]
        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }

        headers.push(
          {
            text: this.$t('subOrder.remainingTime'),
            value: 'remainingTime',
          },
          {
            text: this.$t('subOrder.waitingAllocationSince'),
            value: 'waitingAllocationTime',
          },
          {
            text: this.$t('subOrder.stock'),
            value: 'stock',
            width: '90px',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        return headers
      },
      actions() {
        return {
          waiting: this.$t('subOrder.prepare'),
          confirmed: this.$t('subOrder.resume'),
        }
      },
      headersMobile() {
        let headers = [
          {
            text: this.$t('subOrder.numPreparation'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.origin'),
            value: 'origin',
          },
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusTranslations,
          },
          {
            text: this.$t('order.date'),
            value: 'date',
            filterType: 'date',
          },
        ]

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }

        headers.push(
          {
            text: this.$t('order.products'),
            value: 'total_nb_product',
            sortable: false,
          },
          {
            text: this.$t('subOrder.remainingTime'),
            value: 'remainingTime',
          },
          {
            text: this.$t('subOrder.waitingAllocationSince'),
            value: 'waitingAllocationTime',
          },
          {
            text: this.$t('subOrder.stock'),
            value: 'stock',
            width: '90px',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        return headers
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50, 100],
          'items-per-page-text': this.$t('order.ordersPerPage'),
        }
      },
      orderIdColumn() {
        if (this.$vuetify.breakpoint.width < 450) {
          return 'max-width: 150px'
        }
        if (this.$vuetify.breakpoint.width < 650) {
          return 'max-width: 250px'
        }
        if (this.$vuetify.breakpoint.width < 768) {
          return 'max-width: 100px'
        }
        if (this.$vuetify.breakpoint.width === 768) {
          return 'max-width: 140px'
        }
        if (this.$vuetify.breakpoint.width < 1264) {
          return 'max-width: 250px'
        } else {
          return 'max-width: 300px'
        }
      },
      origin() {
        return [
          { text: this.$t('order.web'), value: 'web' },
          { text: this.$t('order.shop'), value: 'shop' },
          { text: this.$t('order.marketplace'), value: 'marketplace' },
        ]
      },
      headerProps() {
        return {
          sortByText: this.$t('order.sortByText'),
        }
      },
    },
    methods: {
      compute: function (value) {
        // var amount = 0;
        let totalNbProduct = 0
        if (['waiting', 'waiting_allocation'].includes(value.current_status)) {
          for (let item of value.items) {
            //amount += item.row_total_price;
            totalNbProduct += item.product_quantity
          }
        } else {
          for (let item of value.items) {
            //amount += item.row_total_price;
            if (item.validated_quantity) {
              totalNbProduct += item.validated_quantity
            }
          }
        }

        let action = this.actions[this.status[0]] ? this.actions[this.status[0]] : this.$t('order.viewDetails')
        if (this.$route.name === 'waitingParcelReceiptOrders') {
          action = this.$t('subOrder.receive')
        }
        let customer = value.customer.first_name + ' ' + value.customer.last_name

        if (value.shipping_address && this.$route.name === 'waitingParcelReceiptOrders') {
          customer = value.shipping_address.first_name + ' ' + value.shipping_address.last_name
        }
        const remainingTime = new Date(value.date_limit_before_timeout) - new Date()

        return {
          _id: value._id,
          sub_order_id: value.sub_order_id,
          order_id: value.order_id,
          type: value.type,
          location_id: value.location_id,
          date: value.date,
          customer: customer,
          missing_date: new Date().toISOString(),
          missing_remaining_time: '1000h00',
          // amount: amount,
          current_status: value.current_status,
          total_nb_product: totalNbProduct,
          items: value.items,
          date_limit_before_timeout: value.date_limit_before_timeout,
          remainingTime: remainingTime,
          isSelectable:
            !(remainingTime <= 0) || !['waiting', 'confirmed', 'partially_confirmed'].includes(value.current_status), // condition is not remainingTime > 0 because it would be false with undefined date_limit_before_timeout
          waitingAllocationTime: new Date() - new Date(value.status_history[0].date),
          // location: value.source_location_id,
          action: action,
          stock:
            value.stock_location !== undefined ? `${value.stock_location} / ${value.total_quantity_to_prepare}` : 'X',
          preparation_percent: value.preparation_percent,
          origin: value.origin,
          currency: value.currency,
          applied_rule: value.applied_rule,
          pick_pack_ship: value.pick_pack_ship,
        }
      },
      ...mapMutations({
        setLoading: 'SET_LOADING',
      }),
      executeAction: function (subOrderId) {
        this.$router.push('/sub-orders/' + subOrderId)
      },
      list() {
        this.loadingData = true
        this.hideDefaultFooter = false
        this.selected = []

        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        if (sortBy === 'remainingTime') {
          sortBy = 'date_limit_before_timeout'
        }

        if (sortBy === 'waitingAllocationTime') {
          sortBy = 'status_history.0.date'
        }

        let defaultSortDesc =
          this.$route.name === 'waitingParcelReceiptOrders' || this.$route.name === 'preparationHistory'

        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: page,
            itemsPerPage: itemsPerPage,
            // locationId: !this.isCustomerService ? this.currentStore: undefined,
            sortBy: sortBy?.length === 0 ? 'date' : sortBy,
            sortDesc: sortDesc?.length === 0 ? defaultSortDesc : sortDesc,
          },
        }
        if (this.types) {
          myInit.queryStringParameters.types = this.types
        }
        if (this.status) {
          myInit.queryStringParameters.status = this.status
        }
        if (this.$route.path.includes('to-put-back-in-stock')) {
          myInit.queryStringParameters.put_back_in_stock = true
          myInit.queryStringParameters.source_location_id = this.currentStore
        } else if (
          ['waitingOrders', 'confirmedOrders', 'OrdersWaitingPickupCarrier', 'preparationHistory'].includes(
            this.$route.name
          )
        ) {
          myInit.queryStringParameters.source_location_id = this.currentStore
        } else if (this.status.includes('waiting_allocation')) {
          myInit.queryStringParameters.location_id_to_check_for_allocation_eligibility = this.currentStore
          myInit.queryStringParameters.itemsPerPage = false
          if (!sortBy?.length) {
            myInit.queryStringParameters.sortBy = 'stock'
          }
          this.hideDefaultFooter = true
        } else {
          myInit.queryStringParameters.locationId = this.currentStore
        }
        let filters = {}
        for (let filter of this.filters) {
          if (filter.active) {
            if (filter.name === 'type') {
              myInit.queryStringParameters.types = filter.value
            } else if (filter.name === 'current_status') {
              myInit.queryStringParameters.status = filter.value
            } else if (filter.name === 'origin' && filter.value.length) {
              myInit.queryStringParameters.origin = filter.value
            } else {
              filters[filter.name] = filter.value
            }
          }
        }
        if (Object.entries(filters).length !== 0) {
          myInit.queryStringParameters.filters = JSON.stringify(filters)
        }
        this.$Amplify.API.get('sub-orders', '/v1/int/sub-orders', myInit)
          .then((res) => {
            this.subOrders = res.data
            this.totalOrders = res.nbSubOrders
            if (this.totalOrders) {
              this.listSubOrdersPickPackShips()
            }
            this.loadingData = false
            if (this.totalOrders < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalOrders / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
          })
          .catch((e) => {
            this.loadingData = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: 'error during orders loading',
              color: 'error',
            })
          })
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        this.list()
      },
      transform(props) {
        Object.entries(props).forEach(([key, value]) => {
          // Adds leading zero
          const digits = value < 10 ? `0${value}` : value

          props[key] = `${digits}`
        })

        return props
      },
      getStatusTranslation: function (statusValue) {
        var status = this.statusTranslations.find((status) => status.value === statusValue)
        if (status) {
          return status.text
        } else return statusValue
      },
      getTypeTranslation: function (typeValue) {
        var type = this.typeTranslations.find((type) => type.value === typeValue)
        if (type) {
          return type.text
        } else return typeValue
      },
      filter(filters) {
        this.filters = filters
        this.reloadList()
      },
      getStockColor(subOrderPercentagePreparation) {
        let color = 'red'
        if (subOrderPercentagePreparation === 100) {
          color = 'green'
        } else if (![100, 0].includes(subOrderPercentagePreparation)) {
          color = 'orange'
        }
        return color
      },
      getSettingsByCountry(countryIso3) {
        // get the settings of the country selected
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }

        this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
          .then((res) => {
            this.displayFieldSettings = res.display?.customer_information
            this.formFiltersKey = Date.now()
            this.pickPackShipActivated = res.pick_pack_ship.activate ?? false
          })
          .catch((e) => {
            console.log(e)
          })
      },
      listSubOrdersPickPackShips() {
        let { itemsPerPage } = this.options
        const subOrdersIds = this.subOrders.map((subOrder) => subOrder.order_id)
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: 1,
            itemsPerPage: itemsPerPage,
            location_id: this.isCustomerService ? undefined : [this.currentStore],
            status: ['picking', 'packing'],
            sub_orders_id: [subOrdersIds],
          },
        }
        this.$Amplify.API.get('pick-pack-ships', '/v1/int/pick-pack-ships', myInit)
          .then((res) => {
            if (Array.isArray(res.data) && res.data.length) {
              for (const pickPackShip of res.data) {
                for (let pickPackShipSubOrder of pickPackShip.sub_orders) {
                  let matchingSubOrder = this.subOrders.find(
                    (subOrder) => subOrder.order_id === pickPackShipSubOrder.order_id
                  )
                  if (matchingSubOrder) {
                    this.$set(matchingSubOrder, 'pick_pack_ship', {
                      reference: pickPackShip._id,
                      picker: pickPackShip.picker,
                    })
                  }
                }
              }
            }
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('subOrder.errorLoadingPickPackShips'),
              color: 'error',
            })
          })
      },
      togglePickPackShipTooltipFull(index) {
        this.$set(this.showSubOrderInPPSTooltipFull, index, !this.showSubOrderInPPSTooltipFull[index])
        setTimeout(() => {
          this.$set(this.showSubOrderInPPSTooltipFull, index, false)
        }, 3000)
      },
      getPickPackShipMessage(pickPackShipPicker, pickPackShipReference) {
        const hasPicker = pickPackShipPicker
        const translationKey = hasPicker
          ? 'subOrder.alreadyInPickPackShipTable'
          : 'subOrder.alreadyInPickPackShipTableNoPicker'
        const replacingParameters = { picker: hasPicker || undefined, reference: pickPackShipReference }
        return this.$t(translationKey, replacingParameters)
      },
    },
    // we save the current path, to have it when this component will be reused later
    beforeRouteLeave(to, from, next) {
      this.componentPreviousPath = from.path
      next()
    },
  }
</script>
